import React, { useEffect, useState } from 'react';
import styles from './ccf-lyra-header.module.scss';
import { IconButton, Badge } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import CcfAgentState from '../ccf-agent-state/ccf-agent-state';
import {
  CcfNiceLyraLogoIcon,
  CcfNiceLyraLogoGradientIcon,
  CcfHelpIcon,
  CcfNotificationIcon,
  CcfNavigateToSimulatorIcon,
  CcfTooltip,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  getSimulatorTabVisibilityState,
  globalActions,
  getPartnerAccessTabVisibilityState,
} from '../global.app.slice';
import CcfAgentLeg from '../ccf-agent-leg/ccf-agent-leg';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { CcfAcdHeaderPopover } from '../ccf-acd-header/ccf-acd-header-popover';
import { authorizePartnersAccess } from '../login/mock-data/authorizePartnersAccess';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { selectLoginCredentialsState } from '../login/+state/login.slice';
import AWS from 'aws-sdk';
import { awsS3Config } from '../../util/common';

const s3Config = {
  accessKeyId: awsS3Config.accessKeyId,
  secretAccessKey: awsS3Config.secretAccessKey,
  s3SupervisorBucket:
    window.location.hostname === 'd1u959wi0vug3n.cloudfront.net'
      ? 'cxone-supervisor-html-demo-app'
      : 'cxone-supervisor-html-demo-test-app',
  region: awsS3Config.region,
};

AWS.config.update({
  accessKeyId: s3Config.accessKeyId,
  secretAccessKey: s3Config.secretAccessKey,
  region: s3Config.region,
});

const s3 = new AWS.S3();

/* eslint-disable-next-line */
export interface CcfLyraHeaderProps {
  headerText?: string;
  showHeaderText?: boolean;
}

/**
 * Lyra design header component
 * @param props - CcfLyraHeaderProps
 * @returns Lyra header component
 * @example <CcfLyraHeader />
 */
export function CcfLyraHeader(props: CcfLyraHeaderProps) {
  const dispatch = useDispatch();
  const [count, setCount] = useState(0);
  const [showPopOver, setShowPopOver] = useState(false);
  const [popoverFeature, setPopoverFeature] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isSimulatorTabVisible = useSelector(getSimulatorTabVisibilityState);
  const currentUrlPathName = window.location.pathname;
  const url = window.location.host;
  const { setConversationMessage, setShowPartnerAccessPage } = globalActions;
  const [translate] = useTranslator();
  const [conversationReplyMessage, setConversationReplyMessage] =
    useState(null);
  const isPartnerAccessTabVisible = useSelector(
    getPartnerAccessTabVisibilityState
  );
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const loginCredentials = useSelector(selectLoginCredentialsState);
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const assignName = isAuthenticated
    ? `${
        loginCredentialsArray[0].charAt(0).toUpperCase() +
        loginCredentialsArray[0].slice(1).toLowerCase()
      } ${
        loginCredentialsArray[1].charAt(0).toUpperCase() +
        loginCredentialsArray[1].slice(1).toLowerCase()
      }`
    : loginCredentials?.firstName + ' ' + loginCredentials?.lastName;
  /**
   * @example handleConversationNotificationRead()
   */
  const handleConversationNotificationRead = () => {
    setCount(0);
    setConversationReplyMessage(null);
  };

  /**
   * @example navigateToSimulator()
   */
  function navigateToSimulator() {
    if (url === 'localhost:4200') {
      window.open(`/simulator-v2`);
    } else {
      const urlPath = 'https://' + window.location.hostname + `/simulator-v2`;
      window.open(urlPath);
    }
  }

  /**@example fetchS3ConversationMessages() */
  const fetchS3ConversationMessages = async () => {
    try {
      const data = await s3
        .getObject({
          Bucket: s3Config.s3SupervisorBucket,
          Key: `messages/supervisor-to-agent.json`,
          ResponseCacheControl: 'no-cache, no-store, must-revalidate',
        })
        .promise();

      const { data: msgData } = JSON.parse(
        data.Body?.toString('utf-8') || '{}'
      );

      if (msgData && Object.keys(msgData).length > 0) {
        dispatch(setConversationMessage(msgData));
        setConversationReplyMessage(msgData);
        setCount(1);
      }
    } catch (err: any) {
      console.error('S3 error:', err);
    }
  };

  useEffect(() => {
    dispatch(setConversationMessage(null));
    setConversationReplyMessage(null);
    fetchS3ConversationMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @example handleClose()
   */
  const handleClose = () => {
    setAnchorEl(null);
    setShowPopOver(false);
  };
  /**
   *
   * @example navigateToPartnerAccess()
   */
  function navigateToPartnerAccess() {
    dispatch(setShowPartnerAccessPage(true));
  }
  return (
    <header className={styles.lyraHeader}>
      {showPopOver && (
        <CcfAcdHeaderPopover
          popoverFeature={popoverFeature}
          anchorEl={anchorEl}
          handleClose={handleClose}
          conversationReplyMessage={conversationReplyMessage}
          onNotificationRead={handleConversationNotificationRead}
        />
      )}
      <div className={styles.lyraHeaderLeft}>
        <div className={styles.lyraLogoContainer}>
          <CcfNiceLyraLogoIcon
            className={styles.niceIconDefault}
            style={{ width: 24, height: 24 }}
          />
          <CcfNiceLyraLogoGradientIcon
            className={styles.niceIconGradient}
            style={{ width: 24, height: 24 }}
          />
          <span className={styles.lyraTitle}>Agent Workspace</span>
        </div>
      </div>

      <div className={styles.lyraHeaderRight}>
        {isPartnerAccessTabVisible &&
          authorizePartnersAccess.find(
            (x: string) => assignName && x === assignName
          ) && (
            <div
              id="partnerAccessIcon"
              className="simulatorBlock"
              onClick={() => navigateToPartnerAccess()}
            >
              <AdminPanelSettingsIcon
                style={{ fontSize: '1.4rem', color: 'rgb(91 103 112)' }}
              />
            </div>
          )}
        {isSimulatorTabVisible && currentUrlPathName !== '/simulator-v2' && (
          <CcfTooltip title={translate('simulator')} arrow>
            <IconButton
              className={styles.lyraHeaderIcon}
              onClick={() => navigateToSimulator()}
              aria-label="simulatorNavigation"
            >
              <CcfNavigateToSimulatorIcon
                fillColor="rgb(91 103 112 / var(--tw-text-opacity, 1))"
                style={{ fontSize: '1.4rem' }}
              />
            </IconButton>
          </CcfTooltip>
        )}
        <CcfTooltip title={translate('openHelpCenterInNewWindow')} arrow>
          <IconButton className={styles.lyraHeaderIcon}>
            <CcfHelpIcon fillColor="rgb(91 103 112 / var(--tw-text-opacity, 1))" />
          </IconButton>
        </CcfTooltip>
        <CcfTooltip title={translate('notifications')} arrow>
          <IconButton
            className={styles.lyraHeaderIcon}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setShowPopOver(true);
              setPopoverFeature('Notification');
              setAnchorEl(e?.currentTarget);
            }}
          >
            <Badge
              badgeContent={count}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  fontSize: '0.65rem',
                  minWidth: 16,
                  height: 16,
                },
              }}
            >
              <CcfNotificationIcon fillColor="rgb(91 103 112 / var(--tw-text-opacity, 1))" />
            </Badge>
          </IconButton>
        </CcfTooltip>
        <CcfAgentLeg />
        <CcfAgentState agentStateForEmbeededView={false} />
      </div>
      <ToastContainer
        enableMultiContainer
        containerId={'AgentMessageToastContainer'}
        position="top-right"
        newestOnTop={false}
        closeButton={false}
        closeOnClick={false}
        rtl={false}
      />
    </header>
  );
}

export default CcfLyraHeader;
